// ─── Synthetic Research Data Generator ────────────────────────────────────────
// Generates realistic synthetic datasets for quantitative thesis research.
// Core principle: believable social science variance, never perfect correlations.

import { choleskyDecompose } from './matrixUtils'

// ── Random utilities ──────────────────────────────────────────────────────────

// Box-Muller transform for standard normal
function randn() {
  let u, v
  do { u = Math.random() } while (u <= 0)
  do { v = Math.random() } while (v <= 0)
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

// Approximate normal CDF for mapping standard normal to [0,1]
function normCDF(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x))
  const d = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI)
  const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  const p = 1 - d * poly
  return x >= 0 ? p : 1 - p
}

// ── Correlation matrix builder ────────────────────────────────────────────────

const STRENGTH_MAP = { strong: 0.52, moderate: 0.38, weak: 0.17, none: 0.04 }

/**
 * Build a realistic correlation matrix from variable specifications.
 * Applies realism rules: noise on expected correlations, some contradictory
 * findings, moderate IV-IV multicollinearity.
 */
export function buildCorrelationMatrix(variables) {
  const n = variables.length
  const dvIdx = variables.findIndex(v => v.role === 'dv')
  const actualDvIdx = dvIdx >= 0 ? dvIdx : n - 1

  // Start with identity
  const corr = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? 1.0 : 0.0))
  )

  // IV → DV correlations
  for (let i = 0; i < n; i++) {
    if (i === actualDvIdx) continue
    const v = variables[i]
    const base = STRENGTH_MAP[v.strength || 'moderate'] ?? 0.35
    // Random noise ±0.12
    const noise = (Math.random() - 0.5) * 0.24
    const sign = v.direction === 'negative' ? -1 : 1
    // 15% chance of a contradictory finding for weak/moderate predictors
    const flip = (Math.random() < 0.15 && (v.strength === 'weak' || !v.strength)) ? -1 : 1
    const r = Math.max(-0.72, Math.min(0.72, sign * flip * Math.abs(base + noise)))
    corr[i][actualDvIdx] = r
    corr[actualDvIdx][i] = r
  }

  // IV → IV correlations: moderate multicollinearity is realistic
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (i === actualDvIdx || j === actualDvIdx) continue
      const r = (Math.random() * 0.35 + 0.08) * (Math.random() < 0.25 ? -1 : 1)
      corr[i][j] = r
      corr[j][i] = r
    }
  }

  // Shrink off-diagonal by 0.88 to ensure positive definiteness
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i !== j) corr[i][j] *= 0.88
    }
  }

  return corr
}

// ── Multivariate normal sampler ───────────────────────────────────────────────

function sampleMVNormal(n, corrMatrix) {
  const p = corrMatrix.length
  const L = choleskyDecompose(corrMatrix)
  return Array.from({ length: n }, () => {
    const z = Array.from({ length: p }, randn)
    return L.map((row) => row.reduce((sum, lij, j) => sum + lij * z[j], 0))
  })
}

// ── Variable type transformers ─────────────────────────────────────────────────

function toLikert(stdNormal, levels = 5) {
  const p = normCDF(stdNormal)
  return Math.min(levels, Math.max(1, Math.ceil(p * levels)))
}

function toBinary(stdNormal, threshold = 0) {
  return stdNormal > threshold ? 1 : 0
}

function toCount(stdNormal, mean = 10) {
  const lambda = mean
  const val = Math.round(lambda + Math.sqrt(lambda) * stdNormal)
  return Math.max(0, val)
}

function toContinuous(stdNormal, mean = 0, sd = 1, addOutlier = false) {
  let val = mean + sd * stdNormal
  if (addOutlier) val += (Math.random() > 0.5 ? 3.2 : -3.2) * sd
  return Math.round(val * 1000) / 1000
}

// ── Main generator ─────────────────────────────────────────────────────────────

/**
 * Generate a synthetic dataset matching real social science research characteristics.
 *
 * @param {{ variables: Variable[], sampleSize: number, researchDesign?: string }} config
 * @returns {{ rows: object[], variables: Variable[], correlationMatrix: number[][] }}
 *
 * Variable shape: { name, label?, type, role, direction, strength, mean?, sd? }
 * type: 'continuous' | 'likert5' | 'likert7' | 'binary' | 'count'
 * role: 'dv' | 'iv' | 'control' | 'moderator'
 * direction: 'positive' | 'negative' | 'unknown'
 * strength: 'strong' | 'moderate' | 'weak' | 'none'
 */
export function generateDataset(config) {
  const { variables, sampleSize } = config
  if (!variables || variables.length < 2) throw new Error('Need at least 2 variables')

  const corrMatrix = buildCorrelationMatrix(variables)
  const samples = sampleMVNormal(sampleSize, corrMatrix)

  const rows = samples.map((row, idx) => {
    const record = { id: idx + 1 }
    variables.forEach((v, i) => {
      const stdVal = row[i]
      const isOutlier = Math.random() < 0.028 // 2.8% outlier rate
      const isMissing = sampleSize > 30 && Math.random() < 0.018 // 1.8% missing

      if (isMissing) {
        record[v.name] = null
        return
      }

      switch (v.type) {
        case 'likert5':
          record[v.name] = toLikert(stdVal, 5)
          break
        case 'likert7':
          record[v.name] = toLikert(stdVal, 7)
          break
        case 'binary':
          record[v.name] = toBinary(stdVal)
          break
        case 'count':
          record[v.name] = toCount(stdVal, v.mean ?? 10)
          break
        case 'continuous':
        default:
          record[v.name] = toContinuous(stdVal, v.mean ?? 50, v.sd ?? 15, isOutlier)
      }
    })
    return record
  })

  return { rows, variables, correlationMatrix: corrMatrix, sampleSize }
}

// ── CSV / JSON serialization ───────────────────────────────────────────────────

export function datasetToCSV(dataset) {
  const { rows, variables } = dataset
  const headers = ['id', ...variables.map(v => v.name)]
  const lines = [headers.join(',')]
  for (const row of rows) {
    lines.push(headers.map(h => (row[h] == null ? '' : row[h])).join(','))
  }
  return lines.join('\n')
}

export function csvToRows(csv) {
  const lines = csv.trim().split('\n')
  const headers = lines[0].split(',')
  return lines.slice(1).map(line => {
    const vals = line.split(',')
    const record = {}
    headers.forEach((h, i) => {
      const v = vals[i]
      record[h] = v === '' ? null : isNaN(+v) ? v : +v
    })
    return record
  })
}

// ── Auto-infer variables from thesis metadata ─────────────────────────────────

/**
 * Auto-suggest variables from thesis topic, hypotheses, and methodology.
 */
export function inferVariables(thesis) {
  const suggestions = []
  const topic = (thesis.title || '').toLowerCase()

  // DV inference
  if (/performance|efficiency|outcome|output/.test(topic)) {
    suggestions.push({ name: 'performance', label: 'Performance', type: 'continuous', role: 'dv', direction: 'positive', strength: 'strong', mean: 65, sd: 18 })
  } else if (/satisfaction|quality/.test(topic)) {
    suggestions.push({ name: 'satisfaction', label: 'Satisfaction', type: 'likert5', role: 'dv', direction: 'positive', strength: 'strong' })
  } else if (/governance|waste|environment/.test(topic)) {
    suggestions.push({ name: 'governance_quality', label: 'Governance Quality Index', type: 'continuous', role: 'dv', direction: 'positive', strength: 'strong', mean: 55, sd: 20 })
  } else {
    suggestions.push({ name: 'outcome', label: 'Primary Outcome', type: 'continuous', role: 'dv', direction: 'positive', strength: 'strong', mean: 60, sd: 15 })
  }

  // Generic IVs from hypotheses
  const hypotheses = thesis.hypotheses || []
  hypotheses.slice(0, 4).forEach((h, i) => {
    const varName = `predictor_${i + 1}`
    suggestions.push({
      name: varName,
      label: `Predictor ${i + 1}`,
      type: 'likert5',
      role: 'iv',
      direction: 'positive',
      strength: i === 0 ? 'strong' : i === 1 ? 'moderate' : 'weak',
    })
  })

  // Always add a control
  suggestions.push({ name: 'org_size', label: 'Organisation Size', type: 'count', role: 'control', direction: 'positive', strength: 'weak', mean: 250 })
  suggestions.push({ name: 'years_operation', label: 'Years in Operation', type: 'continuous', role: 'control', direction: 'positive', strength: 'none', mean: 12, sd: 8 })

  return suggestions
}
